import { Autocomplete, TextField } from "@mui/material";
import { GoogleApiWrapper } from "google-maps-react";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getBranchDetails } from "../../../../config/Services";
import { useSnackbar } from "../../product/hooks/SnackbarProvider";

const PickupAndDropLocation = (props) => {
  const locId = localStorage.getItem("locId");

  const { pickupLocation, setPickupLocation, dropLocation, setDropLocation } = props;
  const [sessionToken, setSessionToken] = useState(null);

  const [pickupSuggestions, setPickupSuggestions] = useState([]);
  const [dropSuggestions, setDropSuggestions] = useState([]);
  const [autocompleteService, setAutocompleteService] = useState(null);
  const showSnackbar = useSnackbar();
  const dispatch = useDispatch();

  useEffect(() => {
    const autoCompleteService = new props.google.maps.places.AutocompleteService();
    const token = new props.google.maps.places.AutocompleteSessionToken();
    setAutocompleteService(autoCompleteService);
    setSessionToken(token);
  }, [props.google.maps.places.AutocompleteService]);

  const extractAddressComponents = (addressComponents) => {
    let addressLineOne = "";
    let addressLineTwo = "";
    let city = "";
    let state = "";
    let pincode = "";
    let country = "";

    addressComponents?.forEach((component) => {
      const types = component?.types;

      if (types?.includes("street_number")) {
        addressLineOne = component?.long_name;
      }
      if (types?.includes("route")) {
        addressLineOne = addressLineOne
          ? `${addressLineOne} ${component?.long_name}`
          : component?.long_name;
      }
      if (types?.includes("sublocality_level_1") || types?.includes("sublocality")) {
        addressLineTwo = component?.long_name;
      }
      if (types?.includes("locality")) {
        city = component?.long_name;
      }
      if (types?.includes("administrative_area_level_1")) {
        state = component?.long_name;
      }
      if (types?.includes("postal_code")) {
        pincode = component?.long_name;
      }
      if (types?.includes("country")) {
        country = component?.long_name;
      }
    });

    return { addressLineOne, addressLineTwo, city, state, pincode };
  };

  const reverseGeocodeForPincode = (lat, lng, callback) => {
    const geocoder = new props.google.maps.Geocoder();

    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results?.length) {
        const postal = results
          .flatMap((r) => r?.address_components)
          .find((c) => c?.types?.includes("postal_code"));

        callback(postal?.long_name || null);
      } else {
        callback(null);
      }
    });
  };

  const geocodeLocation = (newValue, callback) => {
    const { google } = props;
    const geocoder = new google.maps.Geocoder();

    geocoder.geocode({ placeId: newValue?.place_id }, (results, status) => {
      if (status !== "OK" || !results?.length) {
        showSnackbar("Unable to fetch location coordinates", "error");
        return;
      }

      const result = results[0];
      const location = result?.geometry?.location;

      const extracted = extractAddressComponents(result?.address_components);

      const lat = location.lat();
      const lng = location.lng();

      // fallback if pincode missing
      if (!extracted.pincode) {
        reverseGeocodeForPincode(lat, lng, (postal) => {
          callback({
            ...newValue,
            latitude: lat,
            longitude: lng,
            ...extracted,
            pincode: postal,
          });
        });
      } else {
        callback({
          ...newValue,
          latitude: lat,
          longitude: lng,
          ...extracted,
        });
      }
    });
  };

  const handlePickupChange = (event, newValue) => {
    if (newValue && dropLocation && newValue?.place_id === dropLocation?.place_id) {
      showSnackbar("Pickup and drop locations cannot be the same", "warning");
      return;
    }

    if (newValue) {
      geocodeLocation(newValue, (locationWithCoords) => {
        setPickupLocation(locationWithCoords);
      });
    } else {
      setPickupLocation(null);
    }
  };

  const handleDropChange = (event, newValue) => {
    if (newValue && pickupLocation && newValue?.place_id === pickupLocation?.place_id) {
      showSnackbar("Pickup and drop locations cannot be the same", "warning");
      return;
    }

    if (newValue) {
      geocodeLocation(newValue, (locationWithCoords) => {
        // After geocoding, check if coordinates match pickup
        const isSameLocation =
          pickupLocation?.latitude &&
          pickupLocation?.longitude &&
          locationWithCoords?.latitude === pickupLocation?.latitude &&
          locationWithCoords?.longitude === pickupLocation?.longitude;

        if (isSameLocation) {
          showSnackbar("Pickup and drop locations cannot be the same", "warning");
          setDropLocation(null);
          return;
        }

        setDropLocation((prev) => ({ ...prev, ...locationWithCoords }));
      });
    } else {
      setDropLocation((prev) => ({
        ...prev,
        place_id: null,
        description: null,
        latitude: null,
        longitude: null,
        addressLineOne: null,
        addressLineTwo: null,
        city: null,
        state: null,
        pincode: null,
      }));
    }
  };

  const handleDropInputChange = (event, value) => {
    if (!autocompleteService || !value) {
      setDropSuggestions([]);
      return;
    }

    // Check if it looks like a complete address (contains commas)
    const isCompleteAddress = value.split(",").length > 3;

    if (isCompleteAddress) {
      // Use Geocoding API for complete addresses
      const geocoder = new props.google.maps.Geocoder();
      geocoder.geocode({ address: value }, (results, status) => {
        if (status === "OK" && results?.length) {
          const suggestions = results.map((result) => ({
            description: result.formatted_address,
            place_id: result.place_id,
          }));
          setDropSuggestions(suggestions);
        } else {
          setDropSuggestions([]);
        }
      });
    } else {
      // Use Autocomplete for partial searches
      autocompleteService.getPlacePredictions(
        {
          input: value,
          sessionToken: sessionToken,
          componentRestrictions: { country: "in" }, // Optional: restrict to India
        },
        (results, status) => {
          if (status === "OK" && results) {
            setDropSuggestions(results);
          } else {
            setDropSuggestions([]);
          }
        }
      );
    }
  };

  const handlePickupInputChange = (event, value) => {
    if (!autocompleteService || !value) {
      setPickupSuggestions([]);
      return;
    }

    const isCompleteAddress = value.split(",").length > 3;

    if (isCompleteAddress) {
      const geocoder = new props.google.maps.Geocoder();
      geocoder.geocode({ address: value }, (results, status) => {
        if (status === "OK" && results?.length) {
          const suggestions = results.map((result) => ({
            description: result.formatted_address,
            place_id: result.place_id,
          }));
          setPickupSuggestions(suggestions);
        } else {
          setPickupSuggestions([]);
        }
      });
    } else {
      autocompleteService.getPlacePredictions(
        {
          input: value,
          sessionToken: sessionToken,
          componentRestrictions: { country: "in" },
        },
        (results, status) => {
          if (status === "OK" && results) {
            setPickupSuggestions(results);
          } else {
            setPickupSuggestions([]);
          }
        }
      );
    }
  };

  const getLocationDetails = async () => {
    try {
      const res = await getBranchDetails(locId);
      if (res?.data?.es) {
        showSnackbar("Failed to fetch Location Details", "error");
        return;
      }
      const address = res?.data?.branch?.addresses?.[0];

      // Build a formatted description like Google Places would provide
      const description = [
        address?.addressLine1,
        address?.addressLine2,
        address?.city,
        address?.state,
        address?.pincode,
      ]
        .filter(Boolean)
        .join(", ");

      setPickupLocation(() => ({
        place_id: `branch_${locId}`, // Unique identifier for comparison
        description: description, // Required for Autocomplete display
        latitude: address?.latitude || "",
        longitude: address?.longitude || "",
        addressLineOne: address?.addressLine1 || "",
        addressLineTwo: address?.addressLine2 || "",
        city: address?.city || "",
        pincode: address?.pincode || "",
        state: address?.state || "",
        contactNumber:
          res?.data?.branch?.contacts?.find((c) => c?.contactType === "DEFAULT")?.phoneNo ||
          address?.mobileNumber ||
          "",
      }));
    } catch (err) {
      showSnackbar("Something went wrong", "error");
    }
  };

  useEffect(() => {
    getLocationDetails();
  }, []);

  return (
    <>
      {/* Pickup Location */}
      <div className="tpl-form-section tpl-location-selection-wrapper">
        <label className="tpl-field-label">Pickup Location*</label>
        <Autocomplete
          fullWidth
          value={pickupLocation}
          onChange={handlePickupChange}
          onInputChange={handlePickupInputChange}
          options={pickupSuggestions}
          getOptionLabel={(option) => option.description || ""}
          isOptionEqualToValue={(option, value) => option.place_id === value.place_id}
          disabled
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              className="tpl-location-input"
              placeholder="Search pickup location"
              inputProps={{
                ...params.inputProps,
                title: pickupLocation?.description || "",
              }}
            />
          )}
        />
      </div>

      {/* Drop Location */}
      <div className="tpl-form-section tpl-location-selection-wrapper">
        <label className="tpl-field-label">Drop Location*</label>
        <Autocomplete
          fullWidth
          value={dropLocation}
          onChange={handleDropChange}
          onInputChange={handleDropInputChange}
          options={dropSuggestions}
          getOptionLabel={(option) => option?.description || ""}
          isOptionEqualToValue={(option, value) => option?.place_id === value?.place_id}
          filterOptions={(x) => x} // Disable MUI's built-in filtering
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              className="tpl-location-input"
              placeholder="Search drop location"
              inputProps={{
                ...params.inputProps,
                title: dropLocation?.description || "",
              }}
            />
          )}
        />
      </div>
    </>
  );
};

export default GoogleApiWrapper({
  apiKey: "AIzaSyCXOTKejeUZ-bBgOaoGuhUIGqIRjTRc6Qo",
})(PickupAndDropLocation);
